import { type MouseEvent, type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { TODO_TITLE_TYPE } from './surface'

/**
 * The `titleClassName` of a row whose title is a `TodoViewTitle`. Ends 32px short, so even a long
 * title leaves a stretch of the row a click doesn't open from. A query container, so the title's
 * fade can be sized to it.
 */
export const TODO_VIEW_TITLE_ROW = `line-clamp-none ${TODO_TITLE_TYPE} mr-8 overflow-x-clip whitespace-nowrap [container-type:inline-size]`

/**
 * The title of a To-dos row that opens in a full View, and the only part of the row a click opens
 * it from. Underlined in grey while it's under the pointer, or while its `group/cell` row is
 * keyboard-focused, so the row otherwise reads like any to-do.
 *
 * The text fades over the row title's last 24px like `TODO_TITLE`'s, but the fade is on the text
 * alone, sized to the title's container rather than the text, so the underline drawn beside it
 * never fades. `::after` stretches the target 7px up and down, through the row's padding and
 * border, so it's the row's full height.
 */
export function TodoViewTitle({
  className,
  children,
  onOpen,
}: {
  /** On the text, since an inline-block takes no decoration, such as a line-through, from its title. */
  className?: string
  children: ReactNode
  onOpen: (event: MouseEvent<HTMLSpanElement>) => void
}) {
  return (
    <span
      data-todo-view-target
      className={twMerge(
        'relative inline-block max-w-full cursor-pointer align-top',
        "before:absolute before:inset-x-0 before:bottom-0.5 before:h-px before:content-['']",
        'group-has-[[data-todo-view-target]:hover]/cell:before:bg-foreground-placeholder group-focus-visible/cell:before:bg-foreground-placeholder',
        "after:absolute after:-inset-y-[7px] after:inset-x-0 after:content-['']",
      )}
      onClick={onOpen}
    >
      <span
        className={twMerge(
          'block overflow-hidden [-webkit-mask:linear-gradient(to_right,black_calc(100%_-_24px),transparent)_left/100cqw_100%_no-repeat] [mask:linear-gradient(to_right,black_calc(100%_-_24px),transparent)_left/100cqw_100%_no-repeat]',
          className,
        )}
      >
        {children}
      </span>
    </span>
  )
}
